import http from "./http-common";
import { v4 as uuidv4 } from "uuid";
import { getAuthToken, getCurrentAuthUser } from "./authService";
import { uploadTelemetryService } from "@/utils/uploadTelemetry";
import { captureUploadRequestErrorTelemetry } from "@/utils/uploadErrorTelemetry";
import {
  announceUpload,
  announceUploadTerminal,
} from "@/observability/uploadHook";

let abortController = null;

const buildUploadDiagnosticHeaders = () => {
  const headers = {};
  const connection = typeof navigator !== "undefined"
    ? (navigator.connection || navigator.mozConnection || navigator.webkitConnection)
    : null;

  if (connection?.downlink != null) {
    headers.Downlink = String(connection.downlink);
  }
  if (connection?.rtt != null) {
    headers.RTT = String(connection.rtt);
  }
  if (connection?.effectiveType) {
    headers.ECT = String(connection.effectiveType);
  }
  if (connection?.saveData != null) {
    headers["Save-Data"] = connection.saveData ? "on" : "off";
  }

  return headers;
};

const SESSION_EXPIRED_MESSAGE =
  "Your session expired. Please sign in again and retry the upload.";

const decodeJwtPayload = (token) => {
  try {
    const parts = token.split(".");
    if (parts.length < 2) {
      return null;
    }
    const payloadBase64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = payloadBase64 + "=".repeat((4 - (payloadBase64.length % 4)) % 4);
    const decoded = JSON.parse(atob(padded));
    return decoded;
  } catch (error) {
    console.warn("[LegacyUploadService] Unable to decode token payload for diagnostics", {
      message: error?.message,
    });
    return null;
  }
};

const buildSessionExpiredError = (error) => {
  const wrapped = new Error(SESSION_EXPIRED_MESSAGE);
  wrapped.code = "AUTH_401";
  wrapped.userMessage = SESSION_EXPIRED_MESSAGE;
  wrapped.response = error?.response;
  wrapped.originalError = error;
  return wrapped;
};

const upload = async (formData, onProgress) => {
  const data = new FormData();
  const user = getCurrentAuthUser();
  if (!user) {
    const error = new Error("Sign-in required before starting upload.");
    error.code = "AUTH_REQUIRED";
    error.userMessage = "Please sign in and retry the upload.";
    throw error;
  }

  const token = await getAuthToken({ forceRefresh: true });
  const tokenPayload = decodeJwtPayload(token);
  const nowSeconds = Math.floor(Date.now() / 1000);
  const expSeconds = Number(tokenPayload?.exp);
  const secondsUntilExpiry = Number.isFinite(expSeconds)
    ? expSeconds - nowSeconds
    : null;
  console.info("[LegacyUploadService] Upload token diagnostics", {
    exp: Number.isFinite(expSeconds) ? expSeconds : null,
    now: nowSeconds,
    secondsUntilExpiry,
  });

  if (!formData.files || formData.files.length === 0) {
    console.error("No files provided for upload.");
    throw new Error("No files provided for upload.");
  }

  let totalSize = 0;
  let uploadedSize = 0;
  const startTime = Date.now();

  formData.files.forEach((file) => {
    data.append("files", file);
    totalSize += file.size;
  });

  const providedUploadId = formData.uploadMetadata?.uploadId;
  const resolvedUploadId = providedUploadId || uuidv4();

  const uploadMetadata = {
    ...formData.uploadMetadata,
    uploadId: resolvedUploadId,
    startUploadTime: new Date().toISOString(),
  };
  const requestId = uuidv4();

  data.append(
    "uploadMetadata",
    new Blob([JSON.stringify(uploadMetadata)], {
      type: "application/json",
    })
  );

  console.log("[LegacyUploadService] Starting upload", {
    uploadId: uploadMetadata.uploadId,
    fileCount: formData.files.length,
    totalSize,
  });
  // Observability: announce the upload before uploadTelemetryService.start()
  // so the diagnostics bridge is wired up in time to catch the very first
  // status transition (WAITING → UPLOADING).
  try {
    announceUpload(uploadMetadata.uploadId, {
      uploader: "legacy",
      fileCount: formData.files.length,
      totalSize,
      mimeTypes: Array.from(
        new Set(formData.files.map((f) => f.type).filter(Boolean))
      ),
      packageName: uploadMetadata.packageName,
    });
  } catch (_error) {
    /* observability must never break upload */
  }
  uploadTelemetryService.start(uploadMetadata.uploadId, { totalBytes: totalSize });

  abortController = new AbortController();
  const { signal } = abortController;

  try {
    const response = await http.post("/api/v1/upload", data, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${token}`,
        "x-request-id": requestId,
        "X-Upload-Id": uploadMetadata.uploadId,
        "X-Upload-Session-Id": uploadMetadata.uploadId,
        ...buildUploadDiagnosticHeaders(),
      },
      signal,
      onUploadProgress: (progressEvent) => {
        if (progressEvent.lengthComputable) {
          uploadedSize = progressEvent.loaded;
          if (totalSize > 0 && uploadedSize >= totalSize) {
            // Browser finished sending request body; wait for backend finalization response
            // without showing a stalled/resume warning.
            uploadTelemetryService.markFinalizing(uploadMetadata.uploadId);
          }
          const percentCompleted = Math.round((uploadedSize / totalSize) * 100);

          const elapsedTime = (Date.now() - startTime) / 1000; // in seconds
          const progressPercentage = uploadedSize / totalSize || 0;
          const estimatedTotalTime = elapsedTime / progressPercentage || 0;
          const remainingTime = Math.max(0, estimatedTotalTime - elapsedTime);

          uploadTelemetryService.recordProgress(uploadMetadata.uploadId, {
            loaded: uploadedSize,
          });
          console.debug("[LegacyUploadService] Progress", {
            uploadId: uploadMetadata.uploadId,
            percentCompleted,
            uploadedSize,
            remainingTime,
          });
          onProgress(percentCompleted, remainingTime);
        }
      },
    });
    console.log("[LegacyUploadService] Upload request resolved", {
      uploadId: uploadMetadata.uploadId,
      status: response.status,
    });
    uploadTelemetryService.complete(uploadMetadata.uploadId);
    return response.data;
  } catch (error) {
    if (error?.response?.status === 401) {
      uploadTelemetryService.fail(uploadMetadata.uploadId);
      console.warn("[LegacyUploadService] Upload rejected with 401", {
        uploadId: uploadMetadata.uploadId,
        status: error.response?.status,
      });
      throw buildSessionExpiredError(error);
    }

    uploadTelemetryService.fail(uploadMetadata.uploadId);
    captureUploadRequestErrorTelemetry({
      requestId,
      error,
      fallbackUrl: `${http.defaults.baseURL || ""}/api/v1/upload`,
    });
    if (error.name === 'AbortError') {
      console.warn("[LegacyUploadService] Upload canceled", {
        uploadId: uploadMetadata.uploadId,
        message: error.message,
      });
    } else {
      console.error("[LegacyUploadService] Upload request threw", {
        uploadId: uploadMetadata.uploadId,
        message: error.message,
        code: error.code,
        status: error.response?.status,
      });
    }
    throw error;
  }
};

const cancel = async (uploadId, reason, notes) => {
  try {
    const token = await getAuthToken();

    let response = null;

    try {
      response = await http.post('/api/v1/upload/cancel', { uploadId, reason, notes }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (error) {
      console.error("Failed to cancel the upload on server side:", error.response ? error.response.data : error.message);
    }


    if (abortController) {
      abortController.abort();
      console.log("Upload canceled successfully.");
    }
    try {
      announceUploadTerminal(uploadId, "cancelled", { reason, notes });
    } catch (_error) {
      /* observability must never break cancel */
    }
    uploadTelemetryService.fail(uploadId);

    return response?.data ?? null;
  } catch (error) {
    console.error("Failed to cancel the upload:", error.response ? error.response.data : error.message);
  }
};

const LegacyUploadService = {
  upload,
  cancel,
};

export default LegacyUploadService;
